import { graphql, readInlineData } from 'react-relay';

import { CHARGE_TYPE, PAYMENT_METHOD_TYPE } from '@woovi/shared';

import type { useChargePaymentMethod_charge$key } from './__generated__/useChargePaymentMethod_charge.graphql.ts';

export const useChargePaymentMethod = (
  chargeRef: useChargePaymentMethod_charge$key,
) => {
  const charge = readInlineData<useChargePaymentMethod_charge$key>(
    graphql`
      fragment useChargePaymentMethod_charge on Charge @inline {
        type
        availablePaymentMethods
        paymentMethods {
          ... on PaymentMethodPix {
            method
            status
            brCode
            value
            valueWithDiscount
          }
          ... on PaymentMethodInstallments {
            method
          }
          ... on PaymentMethodCreditCard {
            method
            status
            installmentNumber
            value
          }
          ... on PaymentMethodITP {
            method
            status
            value
            consentId
            authorizationUrl
          }
          ... on PaymentMethodBoleto {
            method
            status
            boletoBarcode
            boletoDigitable
            value
          }
        }
      }
    `,
    chargeRef,
  );

  if (!charge?.paymentMethods) {
    return {
      paymentMethods: [],
      isPixCob: false,
      hasPix: false,
      pixCob: {},
      isPixCreditCard: false,
      isCreditCard: false,
      creditCard: {},
      isInstallments: false,
      installments: {},
      isITP: false,
      itp: {},
      isBoleto: false,
      hasBoleto: false,
      boleto: {},
      pix: {},
    };
  }

  const getPixCob = () =>
    charge.paymentMethods?.find(
      (pm) => pm?.method === PAYMENT_METHOD_TYPE.PIX_COB,
    );

  const getCreditCard = () =>
    charge.paymentMethods?.find(
      (pm) => pm?.method === PAYMENT_METHOD_TYPE.CREDIT_CARD,
    );

  const getInstallments = () =>
    charge.paymentMethods?.find(
      (pm) => pm?.method === PAYMENT_METHOD_TYPE.PIX_CREDIARY,
    );

  const getITP = () =>
    charge.paymentMethods?.find((pm) => pm?.method === PAYMENT_METHOD_TYPE.ITP);

  const getBoleto = () =>
    charge.paymentMethods?.find(
      (pm) => pm?.method === PAYMENT_METHOD_TYPE.BOLETO,
    );

  const pixCob = getPixCob();
  const creditCard = getCreditCard();
  const installments = getInstallments();
  const itp = getITP();
  const boleto = getBoleto();

  const pix = charge.paymentMethods?.find(
    (pm) => pm?.method === PAYMENT_METHOD_TYPE.PIX,
  );

  const isChargeTypePixCreditCard = charge.type === CHARGE_TYPE.PIX_CREDIT;
  const isPixCreditCard =
    isChargeTypePixCreditCard ||
    (charge.paymentMethods?.length === 2 && !!pixCob && !!creditCard);

  const isPixCob = charge.paymentMethods!.length === 1 && !!pixCob?.method;
  const isCreditCard =
    charge.paymentMethods?.length === 1 && !!creditCard?.method;

  const isInstallments =
    charge.paymentMethods?.length === 1 && !!installments?.method;

  const isITP =
    charge.availablePaymentMethods?.length === 1 &&
    charge.availablePaymentMethods.includes(PAYMENT_METHOD_TYPE.ITP);

  const isBoleto = charge.paymentMethods!.length === 1 && !!boleto?.method;

  const hasBoleto = charge.paymentMethods?.find(
    (pm) => pm?.method === PAYMENT_METHOD_TYPE.BOLETO,
  );

  const hasPix = charge.paymentMethods?.find(
    (pm) =>
      pm?.method === PAYMENT_METHOD_TYPE.PIX ||
      pm?.method === PAYMENT_METHOD_TYPE.PIX_COB ||
      pm?.method === PAYMENT_METHOD_TYPE.PIX_COBV,
  );

  return {
    paymentMethods: charge.paymentMethods || [],
    isPixCob,
    hasPix,
    pixCob: pixCob || {},
    isPixCreditCard,
    isCreditCard,
    creditCard: creditCard || {},
    isInstallments,
    installments: installments || {},
    isITP,
    itp: itp || {},
    isBoleto,
    hasBoleto,
    boleto: boleto || {},
    pix: pix || {},
  };
};
